import type * as React from 'react';

/**
 * Accept filter for the file picker. Two shapes are supported:
 *
 *   - A W3C-standard `accept` attribute string — the same form used by the
 *     HTML `<input type="file" accept>` attribute, e.g.
 *     `".pdf,.doc,application/pdf,image/*"`.
 *
 *   - A MIME-to-extensions record matching react-dropzone's native shape,
 *     e.g. `{ 'application/pdf': ['.pdf'] }`. Slightly richer because it
 *     groups extensions under specific MIMEs.
 *
 * Internally normalised to the Record form via `normalize-accept`.
 */
export type FileUploadAccept = string | Record<string, readonly string[]>;

/**
 * How accepted files reached the picker: dropped onto the dropzone, chosen
 * via the OS file picker, or pasted from the clipboard. `FileUploadButton`
 * only ever reports `browse`.
 */
export type FileUploadMethod = 'drag_drop' | 'browse' | 'paste';

/**
 * Metadata about an acceptance, passed as the second argument of
 * `onFilesAccepted`.
 */
export interface FileUploadAcceptedMeta {
  method: FileUploadMethod;
}

/**
 * Reason a file was rejected by the picker. Shape matches react-dropzone so
 * the rejection objects can be passed straight through without mapping.
 */
export interface FileUploadRejection {
  file: File;
  errors: readonly { code: string; message: string }[];
}

/**
 * Stable error codes emitted by the underlying picker. Consumers can switch on
 * these to render localized messages without depending on react-dropzone types.
 */
export const FILE_UPLOAD_ERROR_CODES = {
  FILE_TOO_LARGE: 'file-too-large',
  FILE_TOO_SMALL: 'file-too-small',
  FILE_INVALID_TYPE: 'file-invalid-type',
  TOO_MANY_FILES: 'too-many-files',
} as const;

export type FileUploadErrorCode =
  (typeof FILE_UPLOAD_ERROR_CODES)[keyof typeof FILE_UPLOAD_ERROR_CODES];

/**
 * Props shared by every file-upload primitive in the design system. These
 * mirror the underlying picker's validation knobs; everything else
 * (transport, progress, retry, controlled lists) is feature-owned.
 */
export interface FileUploadCommonProps {
  /**
   * Called with the files that passed validation, plus how they arrived
   * (drag-drop, browse, or paste).
   */
  onFilesAccepted?: (files: File[], meta: FileUploadAcceptedMeta) => void;
  /**
   * Called with the rejected files plus the reason(s).
   */
  onFilesRejected?: (rejections: FileUploadRejection[]) => void;
  /**
   * MIME-type to extension map for the file picker.
   */
  accept?: FileUploadAccept;
  /**
   * Allow more than one file per pick.
   * @default false
   */
  multiple?: boolean;
  /**
   * Hard cap on the number of files accepted per pick (0 = unlimited).
   * @default 0
   */
  maxFiles?: number;
  /**
   * Maximum size per file in bytes.
   * @default Infinity
   */
  maxSize?: number;
  /**
   * Minimum size per file in bytes.
   * @default 0
   */
  minSize?: number;
  /**
   * Disables file selection.
   * @default false
   */
  disabled?: boolean;
  /**
   * Accessible name for the underlying (visually hidden) `<input type="file">`.
   * The native input stays in the accessibility tree, so it needs a name of its
   * own — a labelled visible trigger/dropzone does not name it. Pass a localized,
   * context-specific value (e.g. "Upload profile photo").
   *
   * ⚠️ Must NOT contain PHI or any patient-identifying data — this becomes an
   * `aria-label` attribute in the DOM, which error monitoring, session-recording
   * SDKs, and browser extensions may capture. Use a generic description of the
   * upload's purpose ("Upload prescription"), never patient specifics
   * ("Upload for Jane Smith").
   *
   * @default a localized "Upload file" (resolved via the ambient `<IntlProvider>`;
   * falls back to the English default when no provider is present)
   */
  inputLabel?: string;
}

export interface FileUploadButtonProps
  extends
    FileUploadCommonProps,
    Omit<React.HTMLAttributes<HTMLElement>, keyof FileUploadCommonProps> {
  /**
   * Render the trigger via Radix Slot — the immediate child becomes the
   * clickable trigger and receives the open handler. Use this to compose
   * arbitrary triggers (Button, Tooltip, Badge, etc.).
   * @default false
   */
  asChild?: boolean;
  /**
   * The trigger element. With `asChild`, must be a single React element.
   */
  children: React.ReactNode;
  /**
   * Test identifier for debugging and testing.
   */
  'data-testid'?: string;
}

export type FileDropzoneVariant = 'default' | 'subtle';

export interface FileDropzoneProps
  extends
    FileUploadCommonProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, keyof FileUploadCommonProps> {
  /**
   * Enable pasting files from the clipboard. By default, paste is handled only
   * when the event occurs within the dropzone; use `pasteScope` to opt into
   * document-wide handling.
   * @default false
   */
  enablePaste?: boolean;
  /**
   * Where the paste listener is attached when `enablePaste` is true.
   * Use `document` only when the surrounding feature owns paste handling for
   * the page and disables it while hidden or superseded by another surface.
   * @default 'element'
   */
  pasteScope?: 'element' | 'document';
  /**
   * Use `subtle` on a `bg-surface` container (a dialog body, a card), where
   * the default fill blends in.
   * @default 'default'
   */
  variant?: FileDropzoneVariant;
  /**
   * Custom dropzone content (icon, copy, format hint). When omitted, the
   * dropzone renders as an empty bordered area — consumers are expected to
   * provide the content for their context.
   */
  children?: React.ReactNode;
  /**
   * Test identifier for debugging and testing.
   */
  'data-testid'?: string;
}

/**
 * Where a full-surface drop listens.
 * `'window'` is always the viewport.
 * `'container'` fills the element passed as `container` and nothing else.
 */
export type FileDropSurfaceScope = 'window' | 'container';

/**
 * Drag-only sibling of `FileDropzone`. Same validation contract
 * (`accept`, `multiple`, `maxFiles`, `maxSize`, `minSize`, `onFilesAccepted`,
 * `onFilesRejected`) with no file dialog — browse stays on `FileUploadButton`
 * or a dropzone. Accepted files always report `method: 'drag_drop'`.
 *
 * While enabled it takes every file drop in its boundary, so a nested
 * `FileDropzone` never gets one. Mount one per boundary; set `disabled` to let an
 * inner target take the drop.
 *
 * Drag-only: pair it with a `FileUploadButton` for keyboard users.
 */
export interface FileDropSurfaceProps extends Omit<FileUploadCommonProps, 'inputLabel'> {
  /** `'window'`: the viewport. `'container'`: the `container` element. */
  scope: FileDropSurfaceScope;
  /**
   * Boundary for `scope="container"`. An element, or a ref read after commit
   * so a parent can be the drop target. Must be `position: relative`.
   * Ignored for `scope="window"`.
   *
   * Pass a non-interactive layout element such as a `div`, `main`, `section`
   * or `aside`. Do not pass a button, link, input or other clickable control.
   * Use `FileDropzone` when the same visible surface should support both
   * drag/drop and click-to-browse.
   */
  container?: HTMLElement | React.RefObject<HTMLElement | null> | null;
  /**
   * Overlay copy. Defaults to "Drop files here". Pass product copy from the
   * caller so the primitive stays generic. Visual only; the overlay is
   * `aria-hidden`.
   */
  label?: string;
  /**
   * Optional per-file normalizer applied before drop validation. Use only for
   * generic file-shape cleanup; product validation still belongs in the caller.
   * Drag-only: it receives concrete dropped files and does not promise directory
   * expansion or file path metadata.
   */
  normalizeFile?: (file: File) => File;
  'data-testid'?: string;
}
